import {
  getAllDeliveryWindows,
  getDefaultStockType,
  getIsStockTypeSeparationEnabled,
  getStockTypeFilter,
  getStockTypeLabels,
} from '@app/ducks';
import { getDeliveryWindowsFromProducts, getIsProductInOrderByDelwinIds, getProductsInDelwins, getUniqProductIds } from '@app/logic/Orders';
import { isDefined, isNull } from '@app/utils/is';
import { alphabeticSortByKey } from '@app/utils/objectSort';
import { DeliveryWindow, Order, Product } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

import { SORT_BY_ARTICLE_NUMBER, SORT_BY_DELIVERY_WINDOW } from './constants';
import { StockTypeGroup } from './types';

interface Context {
  sortByValue: string;
  setSortByValue: React.Dispatch<React.SetStateAction<string>>;
  stockTypeGroupsToDisplay: StockTypeGroup[];
  sortBySKU: boolean;
  sortByStockType: boolean;
  sortByByDeliveryWindow: boolean;
  sortedProducts: Product.Full[];
  orderDeliveryWindows: DeliveryWindow.Mixed[];
}

export const useOrderProductsContext = (): Context => {
  const context = React.useContext(OrderProductsContext);
  if (isNull(context)) {
    throw new Error('OrderProductsContext can not be used outside the scope of OrderProductsContextProvider');
  }

  return context;
};

const OrderProductsContext = React.createContext<Context | null>(null);
interface Props {
  orderDetails: Order.Single;
}

export const OrderProductsContextProvider = ({ children, orderDetails }: React.WithChildren<Props>) => {
  const allDelwins = useSelector(getAllDeliveryWindows);
  const stockTypeLabels = useSelector(getStockTypeLabels);
  const defaultStockType = useSelector(getDefaultStockType);
  const stockTypeFilter = useSelector(getStockTypeFilter);
  const isStockTypeSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const [sortByValue, setSortByValue] = React.useState<string>('');
  const productsInfo = Array.isArray(orderDetails.products) ? {} : orderDetails.products;
  const productsOrder = getUniqProductIds(orderDetails);
  const sortByByDeliveryWindow = sortByValue === SORT_BY_DELIVERY_WINDOW;
  const sortByStockType = isStockTypeSeparationEnabled && !sortByByDeliveryWindow;

  const sortBySKU = sortByValue === SORT_BY_ARTICLE_NUMBER;
  const sortedProducts =
    sortBySKU ?
      alphabeticSortByKey(Object.values(productsInfo), 'sku')
    : productsOrder.map(productId => productsInfo[productId]).filter(isDefined);

  const orderDeliveryWindows = React.useMemo(
    () => getDeliveryWindowsFromProducts(orderDetails.order.products, allDelwins),
    [orderDetails, allDelwins],
  );

  const stockTypeGroupsToDisplay = React.useMemo(() => {
    const getProductsAndDelwinIdsByStockType = (stockType: DeliveryWindow.StockType) => {
      const delwinIdsForStockType = orderDeliveryWindows
        .filter(delwin => delwin.stockType === stockType)
        .map(delwin => delwin.deliveryWindow);

      const productsForStockType = sortedProducts.filter(product =>
        getIsProductInOrderByDelwinIds(product.product, delwinIdsForStockType, orderDetails.order.products),
      );
      const orderProductsForStockType = getProductsInDelwins(delwinIdsForStockType, orderDetails.order.products);

      return {
        delwinIds: delwinIdsForStockType,
        orderProducts: orderProductsForStockType,
        products: productsForStockType,
      };
    };

    const stockTypeStockGroup: StockTypeGroup = {
      label: stockTypeLabels.stock,
      type: 'stock',
      ...getProductsAndDelwinIdsByStockType('stock'),
    };
    const stockTypePreorderGroup: StockTypeGroup = {
      label: stockTypeLabels.preorder,
      type: 'preorder',
      ...getProductsAndDelwinIdsByStockType('preorder'),
    };
    const shouldPreferStock = (stockTypeFilter ?? defaultStockType) === 'stock';

    return (shouldPreferStock ? [stockTypeStockGroup, stockTypePreorderGroup] : [stockTypePreorderGroup, stockTypeStockGroup]).filter(
      group => group.products.length,
    );
  }, [stockTypeLabels, stockTypeFilter, defaultStockType, orderDeliveryWindows, sortedProducts, orderDetails]);

  return (
    <OrderProductsContext.Provider
      value={{
        orderDeliveryWindows,
        setSortByValue,
        sortByByDeliveryWindow,
        sortBySKU,
        sortByStockType,
        sortByValue,
        sortedProducts,
        stockTypeGroupsToDisplay,
      }}
    >
      {children}
    </OrderProductsContext.Provider>
  );
};
